import type { Feature, Polygon } from 'geojson';
import { CORE_DECK } from './packs/core';
import { resolveBuiltinZone } from './resolveCard';
import { DEFAULT_SETTINGS, ORIGIN, PROTOCOL_VERSION } from './constants';
import { evaluateStandings } from './series';
import type { Boundary, CardMenuItem, HiderCard, LngLat, PublicState, Sample, Zone } from './protocol';

/**
 * v28 BOLT-156 — the scripted round that explains the game, as DATA.
 *
 * Jake: "we do a great job with the concept of replays. However, I believe a
 * similar concept could be used to create a simple explainer on the Home page…
 * an actual code based scripted game play." And, explicitly: "I'm not
 * suggesting that we use a saved gif."
 *
 * WHY THIS IS DATA AND NOT A DRAWING. The value of the explainer rests entirely
 * on it being the REAL thing. A lookalike drifts the moment a card or the map
 * changes and then teaches a game we no longer ship, which is worth less than
 * nothing. So:
 *
 *  - every ZONE below is computed by `resolveBuiltinZone`, the same resolver
 *    the server runs in a live round, from the same `CORE_DECK` definitions.
 *    Nobody drew these polygons; if Radius 1000 ft ever cuts differently, this
 *    demo cuts differently too, on both platforms, with no edit here.
 *  - the beats are a sequence of SNAPSHOTS - the shape the replay pipeline
 *    already produces - so each renderer just plays `PublicState` frames
 *    through the map component it already ships.
 *  - the card ids are LOOKED UP in `CORE_DECK` and throw if missing, rather
 *    than being a private copy of a card's numbers. Derive, never pin: a
 *    hard-coded card list has cost this repo hours in v18, v24 and v27.
 *
 * Pure TypeScript, no DOM, no socket, no GPS, no server. It cannot start a game
 * or touch session state because it has no way to reach either.
 */

/** The one place the demo's cards are named. Missing ⇒ loud, not silent. */
function card(id: string) {
  const def = CORE_DECK.find((c) => c.id === id);
  if (!def) throw new Error(`explainer: "${id}" is not in CORE_DECK`);
  return def;
}

/* ------------------------------------------------------------------ *
 * The stage: Craig Ranch, the ORIGIN everything else is seeded from.
 * ------------------------------------------------------------------ */

const M_PER_DEG_LAT = 111_320;
const mPerDegLng = (lat: number) => M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);

/** Metres east/north of ORIGIN → a coordinate. */
function at(eastM: number, northM: number): LngLat {
  return {
    lat: ORIGIN.lat + northM / M_PER_DEG_LAT,
    lng: ORIGIN.lng + eastM / mPerDegLng(ORIGIN.lat),
  };
}

/** A square play area centred on ORIGIN. Half-width in metres. */
function square(halfM: number): Boundary {
  const corners: LngLat[] = [at(-halfM, -halfM), at(halfM, -halfM), at(halfM, halfM), at(-halfM, halfM)];
  const ring = [...corners, corners[0]].map((p) => [p.lng, p.lat]);
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
}

/** ~1.4 km across: big enough that four cuts are visible, small enough to read. */
export const EXPLAINER_BOUNDARY: Boundary = square(700);

/** Where the hider ends up. A constant in a fiction - there is no player here. */
const HIDER_AT: LngLat = at(300, 250);

/** The seeker's walk, in order. Each leg is long enough for Slice to be legal. */
const SEEKER_PATH: LngLat[] = [
  at(-520, -480), // 0 start, far corner
  at(-320, -300), // 1 after the first Radius
  at(-120, -140), // 2 after Slice
  at(60, 20), // 3 after Quadrants
  at(230, 180), // 4 close Radius: near enough that the hider is INSIDE it
  at(272, 222), // 5 walks into the zone, up to the hider
];

/* ------------------------------------------------------------------ *
 * The beats
 * ------------------------------------------------------------------ */

export type ExplainerSide = 'left' | 'right';

export interface ExplainerCard {
  /** A `CORE_DECK` id, so both renderers build the SHIPPED card from it. */
  id: string;
  /** Seeker cards enter from the right, hider cards from the left (Jake). */
  side: ExplainerSide;
  /** The hider vetoed it: the card lands and dies. */
  vetoed?: boolean;
  /** Which rail renders it. */
  deck: 'seeker' | 'hider';
}

export interface ExplainerBeat {
  /** Stable id. THE TESTS ASSERT THESE, never pixels (CI is keyless). */
  id: string;
  /** When this beat begins, ms from the start of the loop. */
  atMs: number;
  durationMs: number;
  /** The seeker pin for this beat. */
  seekerAt: LngLat;
  /** The hider pin, shown only while they are running and once they are found. */
  hiderAt: LngLat | null;
  /** The hiding zone after this beat. Null before the round starts. */
  zone: Zone | null;
  card?: ExplainerCard;
  /** One short line. Player-facing, so it is bound by the copy standard. */
  caption: string;
}

/** Beat lengths, in the order they play. Tuned to land inside 10-20 s. */
const MOVE_MS = 900;
const CARD_MS = 1300;
const BEAT_MS = 1100;

function buildBeats(): ExplainerBeat[] {
  const B = EXPLAINER_BOUNDARY;
  const H = HIDER_AT;
  const full: Zone = B as Feature<Polygon>;

  // Every cut below runs the SHIPPED resolver against the SHIPPED card def.
  const afterBigRadius = resolveBuiltinZone(card('radar1km'), { S: SEEKER_PATH[1], H, Z: full });
  const afterSlice = resolveBuiltinZone(card('thermo250'), {
    S: SEEKER_PATH[2],
    H,
    Z: afterBigRadius,
    Pstart: SEEKER_PATH[1],
    Pend: SEEKER_PATH[2],
  });
  // Direction is VETOED, so the zone is deliberately unchanged across it. That
  // is the lesson of the beat: a veto costs the seeker the card, not the map.
  const afterQuadrants = resolveBuiltinZone(card('quadrants'), { S: SEEKER_PATH[3], H, Z: afterSlice });
  const afterCloseRadius = resolveBuiltinZone(card('radar500'), { S: SEEKER_PATH[4], H, Z: afterQuadrants });

  const raw: Omit<ExplainerBeat, 'atMs'>[] = [
    {
      id: 'area',
      durationMs: BEAT_MS,
      seekerAt: SEEKER_PATH[0],
      hiderAt: null,
      zone: null,
      caption: 'One play area. Two teams.',
    },
    {
      id: 'hider-bolts',
      durationMs: MOVE_MS,
      seekerAt: SEEKER_PATH[0],
      hiderAt: H,
      zone: null,
      caption: 'The hider bolts, and hides.',
    },
    {
      id: 'zone-opens',
      durationMs: BEAT_MS,
      seekerAt: SEEKER_PATH[0],
      hiderAt: null,
      zone: full,
      caption: 'Now they could be anywhere.',
    },
    {
      id: 'seeker-hunts',
      durationMs: MOVE_MS,
      seekerAt: SEEKER_PATH[1],
      hiderAt: null,
      zone: full,
      caption: 'The seeker starts hunting.',
    },
    {
      id: 'radius-big',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[1],
      hiderAt: null,
      zone: afterBigRadius,
      card: { id: 'radar1km', side: 'right', deck: 'seeker' },
      caption: 'Cards cut the map down.',
    },
    {
      id: 'freeze',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[1],
      hiderAt: null,
      zone: afterBigRadius,
      card: { id: 'holdup', side: 'left', deck: 'hider' },
      caption: 'The hider fights back. Freeze.',
    },
    {
      id: 'slice',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[2],
      hiderAt: null,
      zone: afterSlice,
      card: { id: 'thermo250', side: 'right', deck: 'seeker' },
      caption: 'Walk, and the map is cut along your path.',
    },
    {
      id: 'direction',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[2],
      hiderAt: null,
      zone: afterSlice,
      card: { id: 'direction', side: 'right', deck: 'seeker', vetoed: true },
      caption: 'Direction. Which way?',
    },
    {
      id: 'veto',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[2],
      hiderAt: null,
      zone: afterSlice,
      card: { id: 'veto', side: 'left', deck: 'hider' },
      caption: 'Vetoed. That one is gone.',
    },
    {
      id: 'quadrants',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[3],
      hiderAt: null,
      zone: afterQuadrants,
      card: { id: 'quadrants', side: 'right', deck: 'seeker' },
      caption: 'Quadrants. Smaller again.',
    },
    {
      id: 'radius-close',
      durationMs: CARD_MS,
      seekerAt: SEEKER_PATH[4],
      hiderAt: null,
      zone: afterCloseRadius,
      card: { id: 'radar500', side: 'right', deck: 'seeker' },
      caption: 'Close now.',
    },
    {
      id: 'closing-in',
      durationMs: MOVE_MS,
      seekerAt: SEEKER_PATH[5],
      hiderAt: null,
      zone: afterCloseRadius,
      caption: 'Into the zone.',
    },
    {
      id: 'found',
      durationMs: 1800,
      seekerAt: SEEKER_PATH[5],
      hiderAt: H,
      zone: afterCloseRadius,
      caption: 'Found!',
    },
  ];

  let t = 0;
  return raw.map((b) => {
    const beat = { ...b, atMs: t };
    t += b.durationMs;
    return beat;
  });
}

export const EXPLAINER_BEATS: ExplainerBeat[] = buildBeats();

export const EXPLAINER_DURATION_MS = EXPLAINER_BEATS.reduce((a, b) => a + b.durationMs, 0);

/** Which beat is on screen at `ms` into the loop. Total, for any input. */
export function beatAt(ms: number): ExplainerBeat {
  const t = ((ms % EXPLAINER_DURATION_MS) + EXPLAINER_DURATION_MS) % EXPLAINER_DURATION_MS;
  let found = EXPLAINER_BEATS[0];
  for (const b of EXPLAINER_BEATS) {
    if (t >= b.atMs) found = b;
    else break;
  }
  return found;
}

/**
 * The frame reduced-motion players get: the LAST one, which is the whole point
 * of the story (the zone is small, the seeker is on the hider, Found). A
 * motion-sensitive player is shown the outcome, not nothing.
 */
export const EXPLAINER_STILL: ExplainerBeat = EXPLAINER_BEATS[EXPLAINER_BEATS.length - 1];

/* ------------------------------------------------------------------ *
 * Beats -> snapshots
 * ------------------------------------------------------------------ */

/**
 * A beat as a `PublicState`, so BOTH renderers can feed it straight into the
 * map component they already ship. This is the "sequence of snapshots" the
 * replay pipeline already produces, and it is why no second renderer exists.
 *
 * The view is the HIDER'S, which is what lets the demo show both actors at
 * once: `selfPin` is a hider-only field, so a seeker snapshot could never carry
 * it. Nothing here touches the privacy invariant - there is no session, no
 * server and no real player, and `snapshotFor` is not involved.
 */
export function explainerState(beat: ExplainerBeat): PublicState {
  const pin = (p: LngLat | null): Sample | null => (p ? { ...p, ts: beat.atMs, acc: 5 } : null);
  return {
    protocolVersion: PROTOCOL_VERSION,
    code: 'DEMO',
    phase: 'active',
    you: { id: 'demo', role: 'hider', isHost: false, name: 'Demo', team: null },
    players: { host: null, hider: null, seeker: null, teamA: null, teamB: null },
    settings: DEFAULT_SETTINGS,
    series: {
      roundIndex: 0,
      totalRounds: 1,
      hiderTeam: null,
      firstHider: 'A',
      standings: evaluateStandings([], DEFAULT_SETTINGS.winCondition),
      history: [],
    },
    boundary: EXPLAINER_BOUNDARY,
    zone: beat.zone,
    eliminated: null,
    pctRemaining: 100,
    zoneAreaSqMeters: 0,
    timer: { serverNow: beat.atMs, headStartEndsAt: null, roundStartedAt: 0, roundEndsAt: null, elapsedMs: beat.atMs, capMs: 0 },
    seekerPin: pin(beat.seekerAt),
    selfPin: pin(beat.hiderAt),
    banner: null,
    ticker: null,
    score: null,
    replay: null,
    pendingPlays: [],
    activeCurse: null,
    radarPing: null,
    portalArmed: false,
    portal: null,
    photoWindows: [],
    photos: [],
    vapidPublicKey: null,
  };
}

/**
 * The beat's card, as the SHIPPED wire types.
 *
 * Not a lookalike and not a snapshot of one: `CardMenuItem` and `HiderCard` are
 * the exact structs the rails already render, built here from the `CORE_DECK`
 * definition. If a card's numbers or family change, this changes with them.
 *
 * A VETOED card is rendered `played` with `vetoed`, because that is the only
 * state the rails hang the X on - it rides the most recently SPENT instance.
 * Every other card is rendered `available`, which is the state both rails draw
 * at full strength. A spent card is deliberately dimmed in a real rail so a
 * player can tell it from a playable one; in a one-card poster there is nothing
 * to tell it apart from, and the dimming just makes it unreadable.
 */
export function explainerSeekerCard(beat: ExplainerBeat): CardMenuItem | null {
  if (!beat.card || beat.card.deck !== 'seeker') return null;
  const def = card(beat.card.id);
  const params = def.behavior.kind === 'builtin' ? def.behavior.params : undefined;
  const num = (k: string) => (typeof params?.[k] === 'number' ? (params[k] as number) : undefined);
  return {
    id: def.id,
    family: def.family ?? 'radar',
    label: def.label,
    effect: def.description,
    radiusFt: num('radiusFt'),
    minFt: num('minFt'),
    cost: def.cost ?? 1,
    status: beat.card.vetoed ? 'played' : 'available',
    playsTotal: 1,
    playsLeft: beat.card.vetoed ? 0 : 1,
    vetoed: beat.card.vetoed,
  };
}

export function explainerHiderCard(beat: ExplainerBeat): HiderCard | null {
  if (!beat.card || beat.card.deck !== 'hider') return null;
  const def = card(beat.card.id);
  if (!def.hider) throw new Error(`explainer: "${def.id}" is not a hider card`);
  return {
    id: def.id,
    kind: def.hider.kind,
    label: def.label,
    effect: def.description,
    bonusMin: def.hider.bonusMin,
    curse: def.hider.curse,
  };
}
